<template>
  <div class="col-md-8 pd-5">
    <div class="card p-1 mb-0 card-leave-schedule">
      <div class="d-flex justify-content-between align-items-center mb-1 border-bottom schedule-head">
        <h5 class="font-bold mb-0">Leave Schedule</h5>
        <div class="d-flex gap-2">
          <div
            v-for="type in tabs"
            :key="type"
            class="tab-item"
            :class="{ active: activeTab === type }"
            @click="activeTab = type"
          >
            {{ type === 'week' ? 'This Week' : type.charAt(0).toUpperCase() + type.slice(1) }}
          </div>
        </div>
      </div>

      <div class="table-responsive">
        <table class="table table-sm mb-0 mt-1">
          <thead class="bg-white">
            <tr class="bg-white">
              <th class="text-uppercase text-dark font-small bg-white">Name</th>
              <th class="text-uppercase text-dark font-small bg-white">Department</th>
              <th class="text-uppercase text-dark font-small bg-white">Leave Type</th>
              <th class="text-uppercase text-dark font-small bg-white">Dates</th>
              <th class="text-uppercase text-dark font-small text-end bg-white">Status</th>
            </tr>
          </thead>
          <tbody class="bg-white">
            <tr v-for="(leave, index) in list" :key="index" class="align-middle">
              <td>
                <div class="d-flex align-items-center gap-1">
                   <div class="avatar-circle" :style="{ backgroundColor: getAvatarColor(leave.name) }">
                      <img v-if="leave.image" :src="leave.image" :alt="leave.name" />
                      <template v-else>
                        {{ getInitials(leave.name) }}
                      </template>
                   </div>
                   <span class="font-bold">{{ leave.name }}</span>
                </div>
              </td>
              <td class="text-dark">{{ leave.department }}</td>
              <td :class="getLeaveTypeClass(leave.leave_type)">{{ leave.leave_type }}</td>
              <td class="text-dark">{{ leave.dates }}</td>
              <td class="text-end">
                <span class="badge rounded-pill" :class="getStatusBadgeClass(leave.status)">
                  {{ formatStatus(leave.status) }}
                </span>
              </td>
            </tr>
            <tr v-if="loading">
              <td colspan="5" class="text-center p-3">
                <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                <span class="ms-1">Loading...</span>
              </td>
            </tr>
            <tr v-else-if="list.length === 0">
              <td colspan="5" class="text-center text-dark p-3">No leave records found.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import hrPayroll from '@/services/modules/hrPayroll/hrPayroll';

const { fetchLeaveSchedule } = hrPayroll();
const route = useRoute();

const tabs = ['today', 'tomorrow', 'week'];
const activeTab = ref('today');
const list = ref([]);
const loading = ref(false);

const getLeaveScheduleData = async (type) => {
    loading.value = true;
    try {
        const query = `?company_id=${route.params.companyId}&type=${type}`;
        const res = await fetchLeaveSchedule(query);
        if (res.status) {
            list.value = res.data;
        }
    } catch (error) {
        console.error("Error fetching leave schedule:", error);
    } finally {
        loading.value = false;
    }
};

watch(activeTab, (newVal) => {
    getLeaveScheduleData(newVal);
});

const getInitials = (name) => {
  if (!name) return '';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
};

const getAvatarColor = (name) => {
  const colors = ['#95a5a6', '#d4ac8d', '#a9c7a9', '#7fb3d5', '#f1948a'];
  if (!name) return colors[0];
  const index = name.length % colors.length;
  return colors[index];
};

const getLeaveTypeClass = (type) => {
  if (!type) return '';
  const t = type.toLowerCase();
  if (t.includes('annual')) return 'text-primary';
  if (t.includes('sick')) return 'text-warning';
  return 'text-secondary';
};

const formatStatus = (status) => {
  if (!status) return '';
  const statuses = {
    'approved': 'APPROVED',
    'pending': 'PENDING',
    'pending_approval': 'PENDING APPROVAL',
    'return': 'RETURNED',
    'reject': 'REJECTED'
  };
  return statuses[status.toLowerCase()] || status;
};

const getStatusBadgeClass = (status) => {
  if (!status) return 'badge-light-secondary';
  switch (status.toLowerCase()) {
    case 'approved':
      return 'badge-light-success';
    case 'pending':
    case 'pending_approval':
      return 'badge-light-warning';
    case 'return':
    case 'reject':
      return 'badge-light-danger';
    default:
      return 'badge-light-secondary';
  }
};

onMounted(() => {
    getLeaveScheduleData(activeTab.value);
});
</script>

<style scoped>
.schedule-head {
  background: #fff;
  padding: 15px;
  border-radius: 8px;
}
.card-leave-schedule {
  min-height: 350px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.05);
}
.tab-item {
  cursor: pointer;
  padding: 0.5rem 0.2rem;
  font-size: 0.9rem;
  color: #82868b;
  font-weight: 500;
  position: relative;
  transition: all 0.2s;
}
.tab-item:hover {
  color: #7367f0;
}
.tab-item.active {
  color: #7367f0;
}
.tab-item.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #7367f0;
}
.font-bold {
  font-weight: 600;
  color: #5e5873;
}
.font-small {
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}
.text-secondary {
  color: #b9b9c3 !important;
}
.avatar-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.8rem;
  font-weight: bold;
  overflow: hidden;
}
.avatar-circle img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.table td {
    padding: 0.75rem 0.5rem;
    border-bottom: 1px solid #ebe9f1;
}
.table th {
    border-bottom: 2px solid #ebe9f1;
}

/* Badge Styles */
.badge-light-success {
    background-color: rgba(40, 199, 111, 0.12);
    color: #28c76f;
}
.badge-light-primary {
    background-color: rgba(115, 103, 240, 0.12);
    color: #7367f0;
}
.badge-light-warning {
    background-color: rgba(255, 159, 67, 0.12);
    color: #ff9f43;
}
.badge-light-danger {
    background-color: rgba(234, 84, 85, 0.12);
    color: #ea5455;
}
.badge-light-secondary {
    background-color: rgba(130, 134, 139, 0.12);
    color: #82868b;
}

/* Text color helpers */
.text-warning {
    color: #ff9f43 !important;
}
.text-primary {
    color: #7367f0 !important;
}
</style>
